import React, { useEffect, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { apiFetch } from '../../services/api/client';
import { DEFAULT_ARTIST_ID } from '../../config/nova-constants';
import { debugSpotifyFunnel } from '../../utils/debug-spotify-funnel';
import { useArtist } from '../../contexts/ArtistContext';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

interface DateRange {
  from: Date;
  to: Date;
}

interface FunnelChartProps {
  artistId?: string;
  dateRange?: DateRange;
  title?: string;
  className?: string;
  refreshKey?: number;
}

interface SpotifyFunnelData {
  playlistTotalReach: number;
  monthlyListeners: number;
  followers: number;
}

interface SpotifyFunnelResponse {
  data: SpotifyFunnelData;
  platformArtistId: string;
  artistName: string;
}

const FunnelChart: React.FC<FunnelChartProps> = ({
  artistId,
  dateRange,
  title = 'Spotify Engagement Funnel',
  className = '',
  refreshKey = 0
}) => {
  const { currentArtistId, availableArtists } = useArtist();
  const [data, setData] = useState<SpotifyFunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get clickhouseArtistId from selected artist
        const selectedArtist = availableArtists.find(a => a.artistId === currentArtistId);
        const clickhouseArtistId = selectedArtist?.clickhouseArtistId;
        
        if (!clickhouseArtistId) {
          setError('ClickHouse artist ID not available');
          setLoading(false);
          return;
        }
        
        // Use debug utility to get detailed information
        const response: SpotifyFunnelResponse = await debugSpotifyFunnel(clickhouseArtistId);
        
        setData(response.data);
      } catch (err: any) {
        setError(err.message || 'Failed to load Spotify funnel data');
        
        setData({
          playlistTotalReach: 1500000,
          monthlyListeners: 750000,
          followers: 125000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentArtistId, availableArtists, dateRange, refreshKey]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  const getChartOption = (): EChartsOption => {
    if (!data) return {};

    // Calculate conversion rates
    const playlistToListeners = data.monthlyListeners > 0 ? 
      ((data.monthlyListeners / data.playlistTotalReach) * 100).toFixed(1) : '0.0';
    const listenersToFollowers = data.followers > 0 ? 
      ((data.followers / data.monthlyListeners) * 100).toFixed(1) : '0.0';

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'item',
        backgroundColor: '#ffffff',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        textStyle: {
          color: '#000000'
        },
        formatter: function(params: any) {
          const { name, value } = params;
          let conversionRate = '';
          
          if (name === 'Monthly Listeners') {
            conversionRate = `<div style="color: #00B050; font-size: 11px; margin-top: 4px; font-weight: 600;">Conversion: ${playlistToListeners}% from Playlist Reach</div>`;
          } else if (name === 'Followers') {
            conversionRate = `<div style="color: #00B050; font-size: 11px; margin-top: 4px; font-weight: 600;">Conversion: ${listenersToFollowers}% from Monthly Listeners</div>`;
          }
          
          return `<div style="padding: 8px;">
            <div style="font-weight: bold; margin-bottom: 6px; color: #000000;">${name}</div>
            <div style="color: #0066CC; margin-bottom: 3px; font-weight: 600;">📊 ${formatNumber(value)}</div>
            ${conversionRate}
          </div>`;
        }
      },
      series: [
        {
          name: 'Spotify Engagement Funnel',
          type: 'funnel',
          left: '10%',
          top: '10%',
          width: '80%',
          height: '80%',
          minSize: '0%',
          maxSize: '100%',
          sort: 'descending',
          gap: 2,
          label: {
            show: true,
            position: 'inside',
            formatter: function(params: any) {
              return `{name|${params.name}}\n{value|${formatNumber(params.value)}}`;
            },
            rich: {
              name: {
                fontSize: 14,
                fontWeight: 'bold',
                color: '#000000', // Black text for better contrast
                lineHeight: 22
              },
              value: {
                fontSize: 16,
                fontWeight: 'bold',
                color: '#000000', // Black text for better contrast
                lineHeight: 24
              }
            }
          },
          labelLine: {
            length: 10,
            lineStyle: {
              width: 1,
              type: 'solid'
            }
          },
          itemStyle: {
            borderColor: '#1C1C1C',
            borderWidth: 2
          },
          emphasis: {
            label: {
              fontSize: 18
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          data: [
            {
              value: data.playlistTotalReach,
              name: 'Playlist Total Reach',
              itemStyle: {
                color: '#00D084' // Brighter Spotify green
              }
            },
            {
              value: data.monthlyListeners,
              name: 'Monthly Listeners',
              itemStyle: {
                color: '#1DB954' // Original Spotify green
              }
            },
            {
              value: data.followers,
              name: 'Followers',
              itemStyle: {
                color: '#00BCD4' // Bright cyan
              }
            }
          ]
        }
      ]
    };
  };

  if (loading) {
    return (
      <div className={`w-full h-full ${className}`}>
        <div className="w-full h-full bg-transparent rounded-lg p-4 flex flex-col">
          <div className="flex items-center mb-6">
            <div>
              <div className="text-lg font-bold text-gray-400">Loading...</div>
              <div className="text-sm text-gray-400">FETCHING FUNNEL DATA</div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={`w-full h-full ${className}`}>
        <div className="w-full h-full bg-transparent rounded-lg p-4 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-red-400">
              <div className="text-sm">{CHART_NO_DATA_FOR_ARTIST}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate conversion rates for summary
  const playlistToListeners = data && data.monthlyListeners > 0 ? 
    ((data.monthlyListeners / data.playlistTotalReach) * 100) : 0;
  const listenersToFollowers = data && data.followers > 0 ? 
    ((data.followers / data.monthlyListeners) * 100) : 0;

  if (!data) {
    return (
      <div className={`w-full h-full ${className}`}>
        <div className="w-full h-full bg-transparent rounded-lg p-4 flex items-center justify-center">
          <div className="text-center text-red-400">
            <div className="text-sm">{CHART_NO_DATA_FOR_ARTIST}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full ${className}`}>
      <div className="w-full h-full bg-transparent p-4 flex flex-col">
        {/* Summary Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-lg font-bold text-cyan-400">
              {formatNumber(data.followers)} Followers
            </div>
            <div className="text-sm text-gray-400">FROM {formatNumber(data.playlistTotalReach)} PLAYLIST REACH</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-green-400 font-medium">
              {playlistToListeners.toFixed(1)}% → {listenersToFollowers.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-400">CONVERSION RATES</div>
          </div>
        </div>

        {/* Funnel Chart */}
        <div className="flex-1 w-full">
          <BaseChart
            option={getChartOption()}
            loading={loading}
            className="w-full h-full"
          />
        </div>

        {/* Show error message if API failed but we have fallback data */}
        {error && data && (
          <div className="mt-2">
            <p className="text-xs text-yellow-400">⚠️ Using test data: {error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FunnelChart;
